"use client";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Clock, Pencil, ChevronRight, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import EditWorkingHoursModal from "./EditWorkingHoursModal";
import { REST_API_URL } from "../../api/config";
import { buildHeaders } from "../../api/buildHeaders";
import { PrimaryButton } from "../../components/buttons/primaryButton/PrimaryButton";

const BASE_URL = REST_API_URL;
const WORKDAYS_GET = `${BASE_URL}/service/my-workdays/`;

function formatTime12h(time24) {
  if (!time24 || typeof time24 !== "string") return "";
  if (
    time24.toUpperCase().includes("AM") ||
    time24.toUpperCase().includes("PM")
  ) {
    return time24.toUpperCase();
  }
  const parts = time24.split(":");
  if (parts.length < 2) return "";
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (isNaN(h) || isNaN(m)) return "";
  const meridian = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12 || 12;
  return `${String(hour12).padStart(2, "0")}:${String(m).padStart(
    2,
    "0",
  )}${meridian}`;
}

export default function YourBusinessWorkingTime() {
  const { t } = useTranslation();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState([]);
  const [workdaysData, setWorkdaysData] = useState([]);
  const [activeDaysCount, setActiveDaysCount] = useState(0);

  const fetchWorkdays = async () => {
    try {
      setLoading(true);
      const token =
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");
      if (!token) {
        window.location.href = "/sign-in";
        return;
      }

      const res = await axios.get(WORKDAYS_GET, { headers: buildHeaders() });

      const data = res.data;
      setWorkdaysData(data);

      const mappedDays = data.map((d) => {
        const prettyDay =
          d.day.charAt(0).toUpperCase() + d.day.slice(1).toLowerCase();

        if (d.active && d.dayTimeRanges?.length > 0) {
          const times = d.dayTimeRanges
            .map((r) => {
              const start = formatTime12h(r.start_time);
              const end = formatTime12h(r.end_time);
              return start && end ? `${start}-${end}` : "";
            })
            .filter(Boolean)
            .join(", ");
          return {
            day: prettyDay,
            originalDay: d.day,
            status: "Open",
            time: times || "—",
            active: true,
            dayTimeRanges: d.dayTimeRanges,
          };
        } else {
          return {
            day: prettyDay,
            originalDay: d.day,
            status: "Closed",
            active: false,
          };
        }
      });

      const activeCount = mappedDays.filter((d) => d.active).length;
      setActiveDaysCount(activeCount);
      setDays(mappedDays);
    } catch (err) {
      console.error("❌ Error fetching workdays:", err);
      if (err.response?.status === 401) {
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = "/sign-in";
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkdays();
  }, []);

  const handleDayClick = (day) => {
    setSelectedDay(day);
    setIsEditModalOpen(true);
  };

  const handleEditAllClick = () => {
    setSelectedDay(null);
    setIsEditModalOpen(true);
  };

  const getDayAbbreviation = (day) => {
    if (!day) return "";
    return t(`workingDays.days.${day.toLowerCase()}`).slice(0, 3);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 flex flex-col gap-4 sm:gap-6 relative overflow-hidden border border-gray-100">
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full -translate-y-12 translate-x-12 opacity-60"></div>
      <div className="absolute bottom-0 left-0 w-16 h-16 bg-gradient-to-tr from-gray-50 to-gray-100 rounded-full translate-y-8 -translate-x-8 opacity-40"></div>

      <div className="flex flex-col sm:flex-row gap-4 justify-between sm:items-center items-start relative z-10">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-[#020637] from-gray-900 to-gray-800 rounded-xl shadow-sm">
            <Clock className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-bold text-xl text-gray-900">
              {t("home.workingDays")}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-gray-600">
                {t("home.daysActive", { count: activeDaysCount })}
              </span>
              <div className="flex items-center gap-1">
                {[...Array(7)].map((_, i) => (
                  <div
                    key={i}
                    className={`w-1.5 h-1.5 rounded-full ${
                      i < activeDaysCount ? "bg-[#020637]" : "bg-gray-300"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        <PrimaryButton
          onClick={handleEditAllClick}
          className="w-full sm:w-auto"
          label={t("home.editAll")}
          rightIcon={<Pencil className="w-4 h-4" />}
        />
      </div>

      {loading ? (
        <div className="relative z-10">
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-10 h-10 border-3 border-gray-200 border-t-gray-900 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600 font-medium">
              {t("home.loadingSchedule")}
            </p>
          </div>
        </div>
      ) : days.length === 0 ? (
        <div className="relative z-10 text-center py-12">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 font-medium">
            {t("home.noWorkingHoursSet")}
          </p>
          <p className="text-gray-500 text-sm mt-1">
            {t("home.addScheduleDescription")}
          </p>
        </div>
      ) : (
        <div className="relative z-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
            {days.map((d, i) => (
              <button
                key={i}
                onClick={() => handleDayClick(d)}
                className="flex flex-col items-center justify-start border-2 border-gray-200 rounded-2xl p-4 gap-3 text-center transition-all duration-300 hover:border-gray-900 hover:bg-gradient-to-b hover:from-gray-50 hover:to-white hover:shadow-lg active:scale-[0.98] cursor-pointer group relative overflow-hidden"
              >
                {d.active && (
                  <>
                    {/* <div className="absolute top-0 left-0 w-1.5 h-full bg-[#020637] opacity-80"></div> */}
                    <div className="absolute top-0 right-0 w-1.5 h-full bg-[#020637] opacity-80"></div>
                  </>
                )}

                <div className="flex flex-col items-center w-full">
                  <div className="mb-1">
                    <span className="text-xs font-semibold text-gray-500 tracking-wide">
                      {getDayAbbreviation(d.originalDay).toUpperCase()}
                    </span>
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 group-hover:text-gray-800 mb-2">
                    {t(`workingDays.days.${d.originalDay.toLowerCase()}`).slice(0, 1)}
                  </h3>

                  {d.status === "Open" ? (
                    <>
                      <div className="mb-2">
                        <span className="bg-gradient-to-r from-[#020637] to-[#020637] text-white text-xs font-semibold rounded-full px-3 py-1.5 shadow-sm">
                          {t("home.statusOpen")}
                        </span>
                      </div>

                      <div className="w-full space-y-1">
                        {d.time
                          .split(",")
                          .map((timeRange, idx) => (
                            <div
                              key={idx}
                              className="text-center text-sm font-semibold text-gray-900 leading-tight whitespace-nowrap"
                            >
                              {timeRange.trim()}
                            </div>
                          ))}
                      </div>
                    </>
                  ) : (
                    <div className="mt-1">
                      <span className="bg-gradient-to-r from-gray-300 to-gray-400 text-gray-700 text-xs font-semibold rounded-full px-3 py-1.5 group-hover:from-gray-400 group-hover:to-gray-500 transition-all duration-300">
                        {t("home.statusClosed")}
                      </span>
                    </div>
                  )}

                  <div className="mt-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-y-0 translate-y-2">
                    <div className="flex items-center gap-1 text-gray-600 text-xs font-medium">
                      <span>{t("home.edit")}</span>
                      <ChevronRight className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <div className="mt-6 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
            <div className="flex flex-col sm:flex-row gap-4 justify-between sm:items-center items-start">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="p-1.5 bg-white rounded-lg border border-gray-200 flex-shrink-0">
                  <Pencil className="w-3.5 h-3.5 text-gray-600" />
                </div>
                <p
                  className="text-sm text-gray-700 leading-snug"
                  dangerouslySetInnerHTML={{
                    __html: t("home.clickDayToEdit", {
                      count: activeDaysCount,
                    }),
                  }}
                />
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#020637] flex-shrink-0"></div>
                  <span className="text-xs font-medium text-gray-600">
                    {t("home.statusOpen")}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-gray-400 flex-shrink-0"></div>
                  <span className="text-xs font-medium text-gray-600">
                    {t("home.statusClosed")}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isEditModalOpen && (
        <EditWorkingHoursModal
          selectedDay={selectedDay?.originalDay}
          initialData={
            selectedDay
              ? workdaysData.find((d) => d.day === selectedDay.originalDay)
              : null
          }
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedDay(null);
            fetchWorkdays();
          }}
        />
      )}
      <script
        dangerouslySetInnerHTML={{
          __html: `
            document.querySelectorAll('.grid > *').forEach((el, index) => {
              el.style.setProperty('--index', index);
            });
          `,
        }}
      />
    </div>
  );
}
